import React, { Fragment, useState, useEffect } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';
import NotFound from './pages/NotFound';
import axios from 'axios';
import { useVariant } from './context/VariantContext';
import type { Customer } from './types/customer';

function AppRouter() {
  const QUESTIONNAIRE_DIR = '/assets/json/';
  const isDev = (import.meta.env.VITE_ENV ?? 'production') === 'development';
  const questionnaireNameDev = import.meta.env.VITE_QUESTIONNAIRENAME_LOCAL_DEV ?? 'form-test';

  const { getVariantElements } = useVariant();

  const jsonUrl = import.meta.env.VITE_JSON_URL || null;
  const DB_ROUTE_BASE = jsonUrl ? (jsonUrl.endsWith('/') ? jsonUrl : jsonUrl + '/') : null;
  const DB_ROUTE_CUSTOMER = DB_ROUTE_BASE ? DB_ROUTE_BASE + 'choice-assistant/' : null;
  const DB_ROUTE_QUESTIONNAIRE = DB_ROUTE_BASE ? DB_ROUTE_BASE + 'questionnaire/' : null;

  const [isLoading, setIsLoading] = useState(true);

  async function loadQuestionnaire(formName: string) {
    async function requestQuestionnaire(name: string, forceLocal = false) {
      if (!forceLocal && DB_ROUTE_QUESTIONNAIRE) {
        return await axios.get(DB_ROUTE_QUESTIONNAIRE + 'fetch/' + name);
      } else {
        return await axios.get(QUESTIONNAIRE_DIR + name + '.json');
      }
    }
    function extractData(response: { status: number; data: Record<string, unknown> }) {
      if (
        response.status !== 200 ||
        !response.data ||
        !response.data['questions'] ||
        !Array.isArray(response.data['questions'])
      )
        return null;
      return response.data;
    }

    let data: Record<string, unknown> | null = null;

    if (formName) {
      formName = formName.replace(/[^a-zA-Z0-9\-_ ]/g, '');
      try {
        data = extractData(await requestQuestionnaire(formName, false));
      } catch {
        console.log(`Questionnaire with identifier '${formName}' not found. Using default form instead.`);
      }
    }
    try {
      if (!data) data = extractData(await requestQuestionnaire(isDev ? 'form-test' : 'form', true));
    } catch {
      console.log('Default questionnaire not found. Using empty questionnaire instead.');
      return null;
    }
    return data;
  }

  useEffect(() => {
    const url = window.location.href;
    const subdomain = import.meta.env.VITE_ORGANIZATION_NAME
      ? import.meta.env.VITE_ORGANIZATION_NAME
      : url.split('.')[0].split('//')[1].split('/')[0];

    const requestUrl = DB_ROUTE_CUSTOMER ? DB_ROUTE_CUSTOMER + subdomain : null;

    const defaultConfig: Customer = {
      primary_color: '#8B2560',
      primary_color_light: '#F9EBF2',
      primary_color_dark: '#711f48',
      secondary_color: '#E55417',
      strong_color: '#E55417',

      library_endpoint: import.meta.env.VITE_LIBRARY_ENDPOINT || null,
      logo_url: '/assets/images/logo.png',
      welcome_text: null,
      questionnaireName: 'form',
      variant: 'algemeen',
      disable_buy_button: false,
      questionnaireData: null,
      questionnaireAllQuestions: null,
      header_images: [],
    };

    const copyConfig = (loadedConfig: Record<string, unknown>): Customer => {
      const retConfig = { ...defaultConfig };
      for (const key in defaultConfig) {
        if (Object.prototype.hasOwnProperty.call(loadedConfig, key)) {
          (retConfig as Record<string, unknown>)[key] = loadedConfig[key];
        }
      }
      return retConfig;
    };

    const addStylingColor = function(varName: string, colorValue: string|null = null) {
      if( colorValue == null ) return "";
      if( /^#[a-fA-F0-9]{3,8}$/.test(colorValue) != true ) return "";
      return "--" + varName + ": " + colorValue + ";\n";
    }

    const applyConfig = () => {
      if (window.customer.questionnaireName === 'form' && isDev) {
        window.customer.questionnaireName = questionnaireNameDev;
      }
      const styling = document.createElement("style");
      let stylingContent = "body {\n";
      stylingContent += addStylingColor("color-primary", window.customer.primary_color);
      stylingContent += addStylingColor("color-primary-light", window.customer.primary_color_light);
      stylingContent += addStylingColor("color-primary-dark", window.customer.primary_color_dark);
      stylingContent += addStylingColor("color-secondary", window.customer.secondary_color);
      stylingContent += addStylingColor("color-strong", window.customer.strong_color);
      stylingContent += "}";
      styling.innerHTML = stylingContent;
      if( window.customerStyling ) window.customerStyling.remove();
      window.customerStyling = styling;
      document.head.appendChild(styling);

      loadQuestionnaire(window.customer.questionnaireName).then((data) => {
        window.customer.questionnaireData = data ? (data['questions'] as { answers: unknown[] }[])[0] as Customer['questionnaireData'] : null;
        window.customer.questionnaireAllQuestions = data ? data['questions'] as Customer['questionnaireAllQuestions'] : null;
        setIsLoading(false);
      });
    };

    if (!requestUrl) {
      window.customer = defaultConfig;
      applyConfig();
      return;
    }

    axios
      .get(requestUrl)
      .then((response) => {
        if (response.data) {
          if (Object.prototype.hasOwnProperty.call(response.data, subdomain))
            window.customer = copyConfig(response.data[subdomain]);
          else if (Object.prototype.hasOwnProperty.call(response.data, 'default'))
            window.customer = copyConfig(response.data['default']);
          else window.customer = defaultConfig;
        } else window.customer = defaultConfig;
      })
      .catch((error) => {
        console.log('error', error);
        window.customer = defaultConfig;
      })
      .finally(applyConfig);
  }, []);

  const VARIANT_COMPONENTS = getVariantElements();

  return (
    <Fragment>
      {!isLoading && (
        <Fragment>
          {<VARIANT_COMPONENTS.headerElement/>}
          <Routes>
            <Route path="/" element={<><Outlet /></>}>
              <Route index element={<VARIANT_COMPONENTS.homeElement/>} />
              <Route path="questions" element={<VARIANT_COMPONENTS.formElement/>} />
              <Route path="results" element={<VARIANT_COMPONENTS.resultsElement/>} />
              {VARIANT_COMPONENTS.productsElement != null && <Route path="products" element={<VARIANT_COMPONENTS.productsElement />} />}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Fragment>
      )}
    </Fragment>
  );
}

export default AppRouter;
